// Administration → Organigramă: the org chart (tree of units) and who heads each unit.
// Approvals go up this chart: the unit's head(s), then each parent unit's head(s), up to the root.
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import { useCatalog } from '../../catalog';
import { Combobox, Confirm, EmptyState, Field, Switch, useToast } from '../../ui';
import { OrgImportModal } from './OrgImport';
import { Icon } from '../../ui/Icon';
import { Loading, PageHead } from './common';

export interface OrgUnit {
  id: number; name: string; active: boolean; parentId: number | null; kind: string; sortOrder: number; members: number;
  heads: { id: number; displayName: string }[];
}
interface Person { id: number; displayName: string; username: string; departments: number[]; heads?: number[] }

export const UNIT_KINDS = ['Conducere', 'Direcție', 'Serviciu', 'Birou', 'Compartiment'];
const kindClass = (k: string) => 'k-' + (({ Conducere: 'lead', 'Direcție': 'dir', Serviciu: 'svc', Birou: 'office' } as Record<string, string>)[k] || 'dept');
const byOrder = (a: OrgUnit, b: OrgUnit) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ro');

/** Small crown marking the head (șef) of a unit. */
export function Crown({ size = 13 }: { size?: number }) {
  return <svg className="oc-crown" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7l4.5 4L12 4l4.5 7L21 7l-2 12H5z" fill="currentColor" /></svg>;
}

/** Chain from a unit up to its root (cycle-safe). */
export function chainUp(units: OrgUnit[], id: number): OrgUnit[] {
  const map = new Map(units.map(u => [u.id, u]));
  const out: OrgUnit[] = []; const seen = new Set<number>();
  let cur = map.get(id);
  while (cur && !seen.has(cur.id)) { out.push(cur); seen.add(cur.id); cur = cur.parentId != null ? map.get(cur.parentId) : undefined; }
  return out;
}

export function OrgChartPage() {
  const toast = useToast();
  const catalog = useCatalog();
  const [units, setUnits] = useState<OrgUnit[] | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | 'root' | null>(null);
  const [zoom, setZoom] = useState(1);
  const scrollRef = React.useRef<HTMLDivElement>(null), treeRef = React.useRef<HTMLDivElement>(null);
  const userZoomed = React.useRef(false);
  /** Whole chart width on screen (not smaller than 45%), then the top unit centred. */
  const fit = () => {
    const sc = scrollRef.current, tr = treeRef.current;
    if (!sc || !tr) return;
    const natural = tr.scrollWidth / (zoom || 1);
    const z = Math.max(0.45, Math.min(1, (sc.clientWidth - 48) / natural));
    setZoom(+z.toFixed(2));
    requestAnimationFrame(() => requestAnimationFrame(centerTop));
  };
  const centerTop = () => {
    const sc = scrollRef.current;
    const top = sc?.querySelector('.oc-tree > ul > li > .oc-node') as HTMLElement | null;
    if (!sc || !top) return;
    const r = top.getBoundingClientRect(), c = sc.getBoundingClientRect();
    sc.scrollLeft += (r.left + r.width / 2) - (c.left + c.width / 2);
    sc.scrollTop = 0;
  };
  const [del, setDel] = useState<OrgUnit | null>(null);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    try {
      const [d, u] = await Promise.all([api.get<OrgUnit[]>('/api/auth/departments'), api.get<Person[]>('/api/auth/users/lookup')]);
      setUnits(d); setPeople(u);
    } catch (e: any) { toast(e.message, 'error'); }
  };
  useEffect(() => { load(); }, []);
  // a chart wider than the screen is fitted (until the user zooms by hand) and the top unit is kept in view
  useEffect(() => {
    if (!units) return;
    const t = setTimeout(() => {
      const sc = scrollRef.current, tr = treeRef.current;
      if (!sc || !tr) return;
      if (!userZoomed.current && tr.scrollWidth > sc.clientWidth + 8) fit(); else if (sc.scrollLeft === 0) centerTop();
    }, 60);
    return () => clearTimeout(t);
  }, [units?.length]);
  /** Runs a change, then reloads the chart and the app catalog. */
  const run = async (fn: () => Promise<any>, ok?: string) => {
    try { const r = await fn(); if (ok) toast(ok, 'ok'); await load(); catalog.reload().catch(() => {}); return r ?? true; }
    catch (e: any) { toast(e.message, 'error'); return null; }
  };

  const children = useMemo(() => {
    const m = new Map<number | null, OrgUnit[]>();
    const ids = new Set((units || []).map(u => u.id));
    for (const u of units || []) { const p = u.parentId != null && ids.has(u.parentId) ? u.parentId : null; if (!m.has(p)) m.set(p, []); m.get(p)!.push(u); }
    m.forEach(l => l.sort(byOrder));
    return m;
  }, [units]);
  const kids = (id: number | null) => children.get(id) || [];
  const isDescendant = (id: number, of: number): boolean => kids(of).some(c => c.id === id || isDescendant(id, c.id));
  const canDrop = (target: number) => drag != null && drag !== target && !isDescendant(target, drag) && units?.find(u => u.id === drag)?.parentId !== target;

  const addUnit = async (parentId: number | null) => {
    const siblings = kids(parentId);
    const r = await run(() => api.post<OrgUnit>('/api/auth/departments', {
      name: parentId == null ? 'Unitate nouă' : 'Compartiment nou', parentId, kind: parentId == null ? 'Conducere' : 'Compartiment',
      sortOrder: siblings.length ? Math.max(...siblings.map(s => s.sortOrder)) + 1 : 0,
    }), 'Unitate creată');
    if (r && typeof r === 'object' && (r as OrgUnit).id) setSel((r as OrgUnit).id);
  };
  const move = (id: number, parentId: number | null) => run(() => api.put('/api/auth/departments/' + id, { parentId }), 'Unitate mutată');
  const onDrop = (e: React.DragEvent, target: number | 'root') => {
    e.preventDefault(); e.stopPropagation();
    const id = drag; setDrag(null); setOver(null);
    if (id == null) return;
    if (target === 'root') { if (units?.find(u => u.id === id)?.parentId != null) move(id, null); return; }
    if (canDropTo(id, target)) move(id, target);
  };
  const canDropTo = (id: number, target: number) => id !== target && !isDescendant(target, id);

  const renderNode = (u: OrgUnit): React.ReactNode => {
    const ch = kids(u.id);
    const dropOk = over === u.id && canDrop(u.id);
    return (
      <li key={u.id}>
        <div className={'oc-node ' + kindClass(u.kind) + (u.active ? '' : ' inactive') + (sel === u.id ? ' sel' : '') + (dropOk ? ' drop' : '') + (drag === u.id ? ' dragging' : '')}
          draggable onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(u.id)); setDrag(u.id); }}
          onDragEnd={() => { setDrag(null); setOver(null); }}
          onDragOver={e => { if (canDrop(u.id)) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (over !== u.id) setOver(u.id); } }}
          onDragLeave={() => { if (over === u.id) setOver(null); }}
          onDrop={e => onDrop(e, u.id)}
          onClick={() => setSel(u.id)} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') setSel(u.id); }}
          title="Clic pentru editare · trageți peste altă unitate pentru a o muta">
          <div className="oc-kind">{u.kind || 'Compartiment'}{!u.active && ' · inactiv'}</div>
          <div className="oc-name">{u.name}</div>
          <div className="oc-heads">
            {u.heads.length ? u.heads.map(h => <span key={h.id} className="oc-head"><Crown />{h.displayName}</span>) : <span className="muted">fără șef</span>}
          </div>
          <div className="oc-meta"><Icon name="users" size={12} />{u.members} {u.members === 1 ? 'membru' : 'membri'}</div>
          <button type="button" className="oc-add" title="Adaugă subordonat" aria-label={`Adaugă subordonat pentru ${u.name}`} onClick={e => { e.stopPropagation(); addUnit(u.id); }}><Icon name="plus" size={12} /></button>
        </div>
        {ch.length > 0 && renderChildren(ch)}
      </li>
    );
  };
  /** Compact layout for big charts: sub-units without subordinates are stacked vertically (in columns of up to 6)
      instead of being spread in one very wide row. */
  const renderChildren = (ch: OrgUnit[]): React.ReactNode => {
    const leaves = ch.filter(c => kids(c.id).length === 0), branches = ch.filter(c => kids(c.id).length > 0);
    if (branches.length === 0 && ch.length >= 2 && ch.length <= 6) return <ul className="oc-stack">{ch.map(renderNode)}</ul>;
    if (leaves.length >= 4 && (branches.length > 0 || leaves.length > 6)) {
      const per = leaves.length <= 12 ? Math.ceil(leaves.length / Math.ceil(leaves.length / 6)) : 6;
      const groups: OrgUnit[][] = [];
      for (let i = 0; i < leaves.length; i += per) groups.push(leaves.slice(i, i + per));
      return <ul>{branches.map(renderNode)}{groups.map((g, i) => <li key={'g' + i} className="oc-group"><ul className="oc-stack">{g.map(renderNode)}</ul></li>)}</ul>;
    }
    return <ul>{ch.map(renderNode)}</ul>;
  };

  if (!units) return <><PageHead title="Organigramă" /><Loading /></>;
  const roots = kids(null);
  const selected = units.find(u => u.id === sel) || null;

  return (
    <div className="oc-page">
      <PageHead title="Organigramă" text="Unitățile instituției și șefii lor. Răspunsul pregătit de o unitate se aprobă, pe rând, de șeful ei și de șefii unităților superioare, până la vârf.">
        <div className="oc-zoom" role="group" aria-label="Zoom">
          <button className="icon-btn" title="Micșorează" onClick={() => (userZoomed.current = true, setZoom(z => Math.max(.3, +(z - .1).toFixed(2))))}>−</button>
          <button className="btn sm ghost" title="Mărime normală" onClick={() => { userZoomed.current = true; setZoom(1); requestAnimationFrame(() => requestAnimationFrame(centerTop)); }}>{Math.round(zoom * 100)}%</button>
          <button className="icon-btn" title="Mărește" onClick={() => (userZoomed.current = true, setZoom(z => Math.min(1.6, +(z + .1).toFixed(2))))}>+</button>
          <button className="btn sm ghost" title="Toată organigrama pe ecran" onClick={() => { userZoomed.current = false; fit(); }}>Potrivește</button>
        </div>
        <button className="btn" onClick={() => setImporting(true)}><Icon name="upload" />Importă organigrama</button>
        <button className="btn primary" onClick={() => addUnit(null)}><Icon name="plus" />Unitate nouă</button>
      </PageHead>
      {importing && <OrgImportModal existing={units} onClose={() => setImporting(false)} onDone={() => { setImporting(false); load(); catalog.reload().catch(() => {}); }} />}

      <div className={'oc-layout ' + (selected ? 'with-panel' : '')}>
        <div className="oc-canvas card" onClick={e => { if (e.target === e.currentTarget) setSel(null); }}>
          <p className="oc-hint muted small"><Icon name="info" size={14} />Clic pe o unitate pentru a o edita. Trageți o unitate peste alta pentru a o muta în subordinea ei; butonul <b>+</b> adaugă un subordonat.</p>
          {drag != null && (
            <div className={'oc-rootzone ' + (over === 'root' ? 'drop' : '')}
              onDragOver={e => { e.preventDefault(); if (over !== 'root') setOver('root'); }} onDragLeave={() => setOver(null)} onDrop={e => onDrop(e, 'root')}>
              <Icon name="arrowUp" size={14} />Nivel superior — plasați aici pentru a face unitatea rădăcină
            </div>
          )}
          {roots.length === 0 ? <EmptyState icon="org" title="Nicio unitate" text="Începeți cu unitatea de conducere." action={<button className="btn primary" onClick={() => addUnit(null)}><Icon name="plus" />Unitate nouă</button>} /> : (
            <div className="oc-scroll" ref={scrollRef} onClick={e => { if (e.target === e.currentTarget) setSel(null); }}>
              <div ref={treeRef} className={'oc-tree' + (drag != null ? ' is-dragging' : '')} style={{ zoom } as React.CSSProperties}>
                <ul>{roots.map(renderNode)}</ul>
              </div>
            </div>
          )}
        </div>
        {selected && <UnitPanel key={selected.id} unit={selected} units={units} people={people} siblings={kids(selected.parentId != null && units.some(u => u.id === selected.parentId) ? selected.parentId : null)}
          run={run} onAdd={() => addUnit(selected.id)} onDelete={() => setDel(selected)} onClose={() => setSel(null)} onSelect={setSel} />}
      </div>

      {del && <Confirm title="Ștergere unitate" danger yesLabel="Șterge" onNo={() => setDel(null)}
        onYes={async () => { const d = del; setDel(null); if (await run(() => api.del('/api/auth/departments/' + d.id), 'Unitate ștearsă')) setSel(d.parentId); }}
        text={<>Ștergeți unitatea <b>{del.name}</b>?{kids(del.id).length > 0 && <> Subordonații ei ({kids(del.id).map(k => k.name).join(', ')}) vor trece direct sub {del.parentId != null ? <b>{units.find(u => u.id === del.parentId)?.name}</b> : 'nivelul superior'}.</>} Utilizatorii rămân, dar nu vor mai fi membri ai acestei unități. Dacă vreți doar să nu mai primească documente, dezactivați-o.</>} />}
    </div>
  );
}

function UnitPanel({ unit, units, people, siblings, run, onAdd, onDelete, onClose, onSelect }: {
  unit: OrgUnit; units: OrgUnit[]; people: Person[]; siblings: OrgUnit[];
  run: (fn: () => Promise<any>, ok?: string) => Promise<any>; onAdd: () => void; onDelete: () => void; onClose: () => void; onSelect: (id: number) => void;
}) {
  const [name, setName] = useState(unit.name);
  const [kind, setKind] = useState(unit.kind || 'Compartiment');
  useEffect(() => { setName(unit.name); setKind(unit.kind || 'Compartiment'); }, [unit.name, unit.kind]);
  const dirty = name.trim() !== unit.name || kind !== (unit.kind || 'Compartiment');
  const save = () => { if (!name.trim()) return; run(() => api.put('/api/auth/departments/' + unit.id, { name: name.trim(), kind: kind.trim() || 'Compartiment' }), 'Unitate salvată'); };

  const headIds = unit.heads.map(h => h.id);
  const setHeads = (ids: number[]) => run(() => api.put(`/api/auth/departments/${unit.id}/heads`, { userIds: ids }), 'Șefi actualizați');
  const members = people.filter(p => p.departments.includes(unit.id)).sort((a, b) => Number(headIds.includes(b.id)) - Number(headIds.includes(a.id)) || a.displayName.localeCompare(b.displayName, 'ro'));
  const headOptions = people.filter(p => !headIds.includes(p.id)).map(p => ({ value: p.id, label: p.displayName, sub: p.username }));
  const kindOptions = [...UNIT_KINDS, ...(UNIT_KINDS.includes(kind) ? [] : [kind])].map(k => ({ value: k, label: k }));

  const path = chainUp(units, unit.id);
  const idx = siblings.findIndex(s => s.id === unit.id);
  const reorder = (dir: -1 | 1) => {
    const list = [...siblings]; const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    run(() => Promise.all(list.map((s, i) => s.sortOrder === i ? null : api.put('/api/auth/departments/' + s.id, { sortOrder: i }))));
  };
  const parent = unit.parentId != null ? units.find(u => u.id === unit.parentId) : null;

  return (
    <aside className="oc-panel card">
      <div className="card-head">
        <Icon name="org" /><h2 className="grow">{unit.name}</h2>
        <button className="icon-btn" onClick={onClose} aria-label="Închide panoul"><Icon name="x" /></button>
      </div>
      <div className="card-body stack" style={{ gap: 14 }}>
        <Field label="Denumire" required>
          <input className="input" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && dirty) save(); }} />
        </Field>
        <Field label="Tip unitate" hint="Alegeți din listă sau tastați alt tip">
          <Combobox value={kind} options={kindOptions} allowAdd clearable={false} onChange={v => setKind(v == null ? 'Compartiment' : String(v))} />
        </Field>
        {dirty && <div className="row"><span className="grow" /><button className="btn sm" onClick={() => { setName(unit.name); setKind(unit.kind || 'Compartiment'); }}>Renunță</button><button className="btn sm primary" disabled={!name.trim()} onClick={save}>Salvează</button></div>}
        <Switch on={unit.active} onChange={v => run(() => api.put('/api/auth/departments/' + unit.id, { active: v }), v ? 'Unitate activată' : 'Unitate dezactivată')} label="Activă (primește documente)" />

        <Field label={<>Șefi <span className="muted">— aprobă documentele unității</span></>}>
          <div className="oc-chips">
            {unit.heads.map(h => (
              <span key={h.id} className="oc-chip"><Crown />{h.displayName}
                <button type="button" title="Elimină" aria-label={`Elimină ${h.displayName}`} onClick={() => setHeads(headIds.filter(x => x !== h.id))}><Icon name="x" size={12} /></button>
              </span>
            ))}
            {!unit.heads.length && <span className="muted small">Fără șef — la aprobare, unitatea se sare.</span>}
          </div>
          <Combobox value={null} options={headOptions} placeholder="Adaugă șef…" onChange={v => { if (v != null) setHeads([...headIds, Number(v)]); }} />
        </Field>

        <div>
          <h4 className="oc-sub">Membri ({members.length})</h4>
          {members.length ? (
            <ul className="oc-members">
              {members.map(m => <li key={m.id}>{headIds.includes(m.id) ? <Crown /> : <Icon name="user" size={13} />}<span className="grow">{m.displayName}</span><span className="muted small">{m.username}</span></li>)}
            </ul>
          ) : <p className="muted small">Niciun membru. Membrii se adaugă din pagina Utilizatori.</p>}
        </div>

        <div>
          <h4 className="oc-sub">Traseu de aprobare</h4>
          <p className="muted small" style={{ margin: '0 0 8px' }}>Un răspuns pregătit aici se aprobă în această ordine:</p>
          <ol className="oc-path">
            {path.map((p, i) => (
              <li key={p.id} className={p.heads.length ? '' : 'skip'}>
                <button type="button" className="oc-path-unit" onClick={() => onSelect(p.id)}>{p.name}</button>
                <span className="small">{p.heads.length ? p.heads.map(h => h.displayName).join(', ') : 'fără șef — se sare'}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="oc-actions">
          <button className="btn sm" onClick={onAdd}><Icon name="plus" />Adaugă subordonat</button>
          <span className="grow" />
          <button className="btn sm danger" onClick={onDelete}><Icon name="trash" />Șterge</button>
        </div>
        <div className="oc-place small">
          <span className="grow muted">{parent ? <>În subordinea <b>{parent.name}</b>.</> : 'Unitate de nivel superior.'} Trageți caseta pentru a o muta.</span>
          {siblings.length > 1 && <span className="oc-order" title="Ordinea între unitățile de același nivel">
            <button className="icon-btn" aria-label="Mută la stânga" title="Mută la stânga" disabled={idx <= 0} onClick={() => reorder(-1)}><Icon name="chevronLeft" size={15} /></button>
            <button className="icon-btn" aria-label="Mută la dreapta" title="Mută la dreapta" disabled={idx < 0 || idx >= siblings.length - 1} onClick={() => reorder(1)}><Icon name="chevronRight" size={15} /></button>
          </span>}
        </div>
      </div>
    </aside>
  );
}
